import { createApi } from '@reduxjs/toolkit/query/react'
import axiosBaseQuery from '@base/axios-base-query'
import jsonApiNormalize from '@base/json-api-normalizer.js'
import { receiveEntities } from '@actions/entity_actions'
import { fieldSelectOptionDeleted } from '@actions/field_select_option_actions'

export interface FieldSelectOption {
  id: number
  label: string
}

export interface FetchFieldSelectOptionsArgs {
  tableId: string | number
  fieldId: string | number
}

export interface AddFieldSelectOptionArgs extends FetchFieldSelectOptionsArgs {
  label: string
}

export interface UpdateFieldSelectOptionArgs extends FetchFieldSelectOptionsArgs {
  optionId: number
  label?: string
  position?: number
}

export const fieldSelectOptionsApi = createApi({
  reducerPath: 'fieldSelectOptionsApi',
  baseQuery: axiosBaseQuery({ baseUrl: '/api/v2/' }),
  endpoints: build => ({
    fetchOptions: build.query<FieldSelectOption[], FetchFieldSelectOptionsArgs>({
      query: ({ tableId, fieldId }) => ({
        url: `tables/${tableId}/fields/${fieldId}/field_select_options`,
        method: 'get',
        requestId: `fetch-options-${tableId}-${fieldId}`,
      }),
      transformResponse: (response: any) => response,
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        // also ensure our normalized data slice gets updated when we add an option
        try {
          const { data } = await queryFulfilled
          const normalized = jsonApiNormalize(data, { camelizeKeys: false, camelizeTypeValues: false })
          dispatch(receiveEntities(normalized))
        } catch {
          // no-op on error
        }
      },
    }),
    addOption: build.mutation<FieldSelectOption, AddFieldSelectOptionArgs>({
      query: ({ tableId, fieldId, label }) => ({
        url: `tables/${tableId}/fields/${fieldId}/field_select_options`,
        method: 'post',
        data: {
          data: {
            type: 'field_select_options',
            attributes: { label },
          },
        },
        
        requestId: `add-option-${tableId}-${fieldId}`,
      }),
      transformResponse: (response: any) => response,
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        // also ensure our normalized data slice gets updated when we add an option
        try {
          const { data } = await queryFulfilled
          const normalized = jsonApiNormalize(data, { camelizeKeys: false, camelizeTypeValues: false })
          dispatch(receiveEntities(normalized))
        } catch {
          // no-op on error
        }
      },
    }),
    updateOption: build.mutation<FieldSelectOption, UpdateFieldSelectOptionArgs>({
      query: ({ tableId, fieldId, optionId, label, position }) => ({
        url: `tables/${tableId}/fields/${fieldId}/field_select_options/${optionId}`,
        method: 'patch',
        data: {
          data: {
            id: String(optionId),
            type: 'field_select_options',
            attributes: Object.assign({}, label !== undefined ? { label } : {}, position !== undefined ? { position } : {}),
          },
        },
        
        requestId: `update-option-${tableId}-${fieldId}-${optionId}`,
      }),
      transformResponse: (response: any) => response,
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          const normalized = jsonApiNormalize(data, { camelizeKeys: false, camelizeTypeValues: false })
          dispatch(receiveEntities(normalized))
        } catch {
          // no-op on error
        }
      },
    }),
    removeOption: build.mutation<
      any,
      { tableId: string | number; fieldId: string | number; optionId: number }
    >({
      query: ({ tableId, fieldId, optionId }) => ({
        url: `tables/${tableId}/fields/${fieldId}/field_select_options/${optionId}`,
        method: 'delete',
        requestId: `remove-option-${tableId}-${fieldId}-${optionId}`,
      }),
      transformResponse: (response: any) => response,
        async onQueryStarted({ optionId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled
            // Ensure our normalized data slices get updated when we remove an option
            dispatch(receiveEntities({ field_select_options: { [optionId]: null } }))
            
            // Dispatch action to allow rows reducer to remove references to deleted options
            const normalized = jsonApiNormalize(data, { camelizeKeys: false, camelizeTypeValues: false }) as any
            if (Object.keys(normalized.field_select_options).length) {     
              dispatch(fieldSelectOptionDeleted(normalized as any))
            }
          } catch {
            // no-op on error
          }
        },
    }),
  }),
})

export const { useFetchOptionsQuery, useAddOptionMutation, useRemoveOptionMutation, useUpdateOptionMutation } = fieldSelectOptionsApi
